import React, { useMemo, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { StatusPill, formatProgressTone, useFormPalette } from './DynamicFormPrimitives';
import PublicFormShell from './PublicFormShell';
import { StyledSubmitButton } from './PublicFormStyles';
import FormStatusBanner, { resolveFormStatusBannerKey } from './FormStatusBanner';
import { EmailMagicLinkGateScreen, AuthGateScreen, DisabledGateScreen, NotPublishedScreen } from '../../access/PublicAccessGateScreens';
import { createAgentAdapter } from './adapters/agentFormAdapter';
import { createTriggerAdapter } from './adapters/triggerFormAdapter';
import usePublicForm from './usePublicForm';

export default function PublicFormPage() {
  const { token, componentId, instanceId } = useParams();
  const [searchParams] = useSearchParams();
  const preview = searchParams.get('preview') === 'true';
  const palette = useFormPalette();
  const navigate = useNavigate();

  const resourceId = token || (instanceId ? `${componentId}/${instanceId}` : componentId);
  const adapter = useMemo(
    () => (token ? createAgentAdapter(token) : createTriggerAdapter(componentId, instanceId, { preview })),
    [token, componentId, instanceId, preview]
  );

  const onInstanceCreated = useCallback((newInstanceId) => {
    if (componentId && newInstanceId) {
      navigate(`/dynamic-form/${componentId}/${newInstanceId}`, { replace: true });
    }
  }, [componentId, navigate]);

  const {
    loading,
    submitting,
    uploadingFieldKey,
    form,
    answers,
    validation,
    error,
    gateState,
    submitted,
    readOnly,
    canSubmit,
    canEdit,
    coverImageUrl,
    capabilities,
    onChangeAnswer,
    onFieldBlur,
    onUpload,
    saveDraft,
    submit,
    refresh,
    requestMagicLink,
    onSignedIn,
    autoSaveStatus,
  } = usePublicForm({ adapter, resourceId, onInstanceCreated });

  const authGate = useMemo(() => {
    if (gateState === 'not_published') return <NotPublishedScreen />;
    if (gateState === 'disabled') return <DisabledGateScreen />;
    if (gateState === 'email_required') {
      return <EmailMagicLinkGateScreen onRequestMagicLink={requestMagicLink} />;
    }
    if (gateState === 'auth_required') {
      return <AuthGateScreen onSignedIn={onSignedIn} />;
    }
    return null;
  }, [gateState, requestMagicLink, onSignedIn]);

  const statusBannerKey = resolveFormStatusBannerKey(form);

  return (
    <PublicFormShell
      loading={loading}
      error={error}
      form={form}
      answers={answers}
      validation={validation}
      submitted={submitted}
      submitting={submitting}
      readOnly={readOnly}
      uploadingFieldKey={uploadingFieldKey}
      coverImageUrl={coverImageUrl}
      isPreview={!!form?.isPreview}
      onChange={onChangeAnswer}
      onFieldBlur={onFieldBlur}
      onUpload={capabilities.canUpload ? onUpload : undefined}
      authGate={authGate}
      statusBanner={statusBannerKey ? <FormStatusBanner status={statusBannerKey} /> : null}
      headerRightSlot={
        capabilities.hasProgressStatus && form ? (
          <StatusPill
            label={form.progressStatus || 'ACTIVE'}
            tone={formatProgressTone(form.progressStatus || 'ACTIVE')}
          />
        ) : null
      }
      stickyHeader={
        form && (capabilities.canRefresh || capabilities.canSaveDraft || autoSaveStatus) ? (
          <>
            {autoSaveStatus === 'saving' && (
              <Typography variant="caption" sx={{ color: palette.textSecondary, fontSize: '11px', opacity: 0.7 }}>
                Saving...
              </Typography>
            )}
            {autoSaveStatus === 'saved' && (
              <Typography variant="caption" sx={{ color: palette.textSecondary, fontSize: '11px', opacity: 0.7 }}>
                Draft saved
              </Typography>
            )}
            {autoSaveStatus === 'restored' && (
              <Typography variant="caption" sx={{ color: '#4caf50', fontSize: '11px' }}>
                Draft restored
              </Typography>
            )}
            {capabilities.canRefresh && (
              <Button
                size="small"
                variant="text"
                onClick={refresh}
                disabled={submitting}
                sx={{
                  textTransform: 'none',
                  color: palette.textSecondary,
                  fontSize: '13px',
                  minWidth: 'auto',
                  '&:hover': { color: palette.textPrimary, bgcolor: 'transparent' },
                }}
              >
                Refresh
              </Button>
            )}
            {capabilities.canSaveDraft && (
              <Button
                size="small"
                variant="outlined"
                onClick={saveDraft}
                disabled={submitting || readOnly || !canEdit}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderColor: palette.border,
                  color: palette.textPrimary,
                  borderRadius: '8px',
                  px: 2,
                  '&:hover': {
                    borderColor: palette.textSecondary,
                    bgcolor: palette.chipBg,
                  },
                }}
              >
                {submitting ? 'Saving...' : 'Save draft'}
              </Button>
            )}
          </>
        ) : null
      }
      actions={
        statusBannerKey ? null : (
          <StyledSubmitButton
            palette={palette}
            isLoading={submitting}
            disabled={submitting || readOnly || !canSubmit}
            onClick={submit}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </StyledSubmitButton>
        )
      }
    />
  );
}
